const LOG_LEVEL_NAMES = [
  "silent",
  "error",
  "warn",
  "info",
  "debug",
  "verbose",
] as const;

export type LogLevelName = (typeof LOG_LEVEL_NAMES)[number];

export const REQUIRED_KEYS = [
  "FIREBASE_API_KEY",
  "FIREBASE_AUTH_DOMAIN",
  "FIREBASE_PROJECT_ID",
  "FIREBASE_STORAGE_BUCKET",
  "FIREBASE_MESSAGING_SENDER_ID",
  "FIREBASE_APP_ID",
  "FIREBASE_VAPID_KEY",
  "TURNSTILE_SITE_KEY",
  "GOOGLE_CLIENT_ID",
] as const;

export type RuntimeEnv = {
  readonly FIREBASE_API_KEY: string;
  readonly FIREBASE_AUTH_DOMAIN: string;
  readonly FIREBASE_PROJECT_ID: string;
  readonly FIREBASE_STORAGE_BUCKET: string;
  readonly FIREBASE_MESSAGING_SENDER_ID: string;
  readonly FIREBASE_APP_ID: string;
  readonly FIREBASE_VAPID_KEY: string;
  readonly TURNSTILE_SITE_KEY: string;
  readonly GOOGLE_CLIENT_ID: string;
  readonly APP_NAME: string;
  readonly LOG_LEVEL: LogLevelName;
  readonly LOG_STACK_TRACE: boolean;
  readonly LOG_STACK_DEPTH: number;
  readonly SENTRY_DSN: string;
  readonly SENTRY_ENVIRONMENT: string;
};

type RawEnv = Record<string, string | undefined>;

function logLevel(raw: string | undefined): LogLevelName {
  if (raw === undefined || raw === "") return "warn";
  if ((LOG_LEVEL_NAMES as readonly string[]).includes(raw)) {
    return raw as LogLevelName;
  }
  console.warn(
    `[config] invalid LOG_LEVEL "${raw}" — must be one of ${LOG_LEVEL_NAMES.join(", ")}; falling back to "warn"`,
  );
  return "warn";
}

export function parseEnv(raw: RawEnv): { env: RuntimeEnv; missing: string[] } {
  const missing = REQUIRED_KEYS.filter((key) => !raw[key]);

  const env: RuntimeEnv = Object.freeze({
    FIREBASE_API_KEY: raw.FIREBASE_API_KEY ?? "",
    FIREBASE_AUTH_DOMAIN: raw.FIREBASE_AUTH_DOMAIN ?? "",
    FIREBASE_PROJECT_ID: raw.FIREBASE_PROJECT_ID ?? "",
    FIREBASE_STORAGE_BUCKET: raw.FIREBASE_STORAGE_BUCKET ?? "",
    FIREBASE_MESSAGING_SENDER_ID: raw.FIREBASE_MESSAGING_SENDER_ID ?? "",
    FIREBASE_APP_ID: raw.FIREBASE_APP_ID ?? "",
    FIREBASE_VAPID_KEY: raw.FIREBASE_VAPID_KEY ?? "",
    TURNSTILE_SITE_KEY: raw.TURNSTILE_SITE_KEY ?? "",
    GOOGLE_CLIENT_ID: raw.GOOGLE_CLIENT_ID ?? "",
    APP_NAME: raw.APP_NAME || "Ieup",
    LOG_LEVEL: logLevel(raw.LOG_LEVEL),
    LOG_STACK_TRACE: raw.LOG_STACK_TRACE === "true",
    LOG_STACK_DEPTH: Number(raw.LOG_STACK_DEPTH) || 3,
    SENTRY_DSN: raw.SENTRY_DSN ?? "",
    SENTRY_ENVIRONMENT: raw.SENTRY_ENVIRONMENT || "production",
  });

  return { env, missing: [...missing] };
}

export function readRuntimeEnv(): RawEnv {
  return (globalThis as { __ENV__?: RawEnv }).__ENV__ ?? {};
}

const parsed = parseEnv(readRuntimeEnv());

export const env = parsed.env;
export const missingEnvKeys: readonly string[] = parsed.missing;
